import { useEffect, useRef, useState, useCallback } from 'react'
import GameRanking from '../components/GameRanking'
import { saveScore, loadGlobalRanking, loadPersonalRanking } from '../utils/firestoreService'
import UserHeader from '../components/UserHeader'
import GameStats from '../components/GameStats'
import RankingModal from '../components/RankingModal'
import MobileControls from '../components/MobileControls'
import './Pong.css'

const CANVAS_WIDTH = 800
const CANVAS_HEIGHT = 600
const PADDLE_WIDTH = 15
const PADDLE_HEIGHT = 100
const BALL_SIZE = 15
const PADDLE_SPEED = 8
const BALL_SPEED_INITIAL = 5

export default function Pong({ user, onBackToMenu }) {
  const canvasRef = useRef()
  const [score, setScore] = useState(0)
  const [aiScore, setAiScore] = useState(0)
  const [gameOver, setGameOver] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [playerPaddle, setPlayerPaddle] = useState({ y: CANVAS_HEIGHT / 2 - PADDLE_HEIGHT / 2 })
  const [aiPaddle, setAiPaddle] = useState({ y: CANVAS_HEIGHT / 2 - PADDLE_HEIGHT / 2 })
  const [ball, setBall] = useState({
    x: CANVAS_WIDTH / 2,
    y: CANVAS_HEIGHT / 2,
    dx: BALL_SPEED_INITIAL,
    dy: BALL_SPEED_INITIAL
  })
  const [globalRanking, setGlobalRanking] = useState([])
  const [personalRanking, setPersonalRanking] = useState([])
  const [showRanking, setShowRanking] = useState(false)
  const [savingScore, setSavingScore] = useState(false)
  const [scoreSaved, setScoreSaved] = useState(false)
  const keysPressed = useRef({})
  const gameLoopRef = useRef()
  const mobileMovementRef = useRef({ up: false, down: false })

  const movePaddles = useCallback(() => {
    if (gameOver || isPaused) return

    // Player movement
    setPlayerPaddle(prev => {
      let newY = prev.y
      if (keysPressed.current['ArrowUp'] || mobileMovementRef.current.up) {
        newY = Math.max(0, prev.y - PADDLE_SPEED)
      }
      if (keysPressed.current['ArrowDown'] || mobileMovementRef.current.down) {
        newY = Math.min(CANVAS_HEIGHT - PADDLE_HEIGHT, prev.y + PADDLE_SPEED)
      }
      return { y: newY }
    })

    // AI movement (follows ball)
    setAiPaddle(prev => {
      const aiCenter = prev.y + PADDLE_HEIGHT / 2
      const ballCenter = ball.y
      let newY = prev.y

      if (aiCenter < ballCenter - 20) {
        newY = Math.min(CANVAS_HEIGHT - PADDLE_HEIGHT, prev.y + PADDLE_SPEED * 0.7)
      } else if (aiCenter > ballCenter + 20) {
        newY = Math.max(0, prev.y - PADDLE_SPEED * 0.7)
      }
      return { y: newY }
    })
  }, [ball.y, gameOver, isPaused])

  const loadRankings = useCallback(async () => {
    try {
      const [global, personal] = await Promise.all([
        loadGlobalRanking('pong'),
        loadPersonalRanking(user?.uid, 'pong')
      ])
      setGlobalRanking(global)
      setPersonalRanking(personal)
    } catch (error) {
      console.error('Error loading rankings:', error)
    }
  }, [user])

  const saveGameScore = useCallback(async (finalScore) => {
    if (!user || finalScore === 0) {
      console.log('Not saving Pong score:', { user: !!user, score: finalScore })
      return
    }
    
    try {
      setSavingScore(true)
      console.log('Saving Pong score:', finalScore)
      await saveScore(user, finalScore, 1, aiScore, 'pong')
      await loadRankings()
      setSavingScore(false)
      setScoreSaved(true)
      
      setTimeout(() => {
        setShowRanking(true)
      }, 1500)
    } catch (error) {
      console.error('Error saving score:', error)
      setSavingScore(false)
    }
  }, [user, aiScore, loadRankings])

  const updateBall = useCallback(() => {
    if (gameOver || isPaused) return

    setBall(prev => {
      let newX = prev.x + prev.dx
      let newY = prev.y + prev.dy
      let newDx = prev.dx
      let newDy = prev.dy

      // Wall collision (top/bottom)
      if (newY <= 0 || newY >= CANVAS_HEIGHT - BALL_SIZE) {
        newDy = -newDy
      }

      // Player paddle collision (left side with offset)
      if (
        newX <= 20 + PADDLE_WIDTH &&
        newX >= 20 &&
        newY + BALL_SIZE >= playerPaddle.y &&
        newY <= playerPaddle.y + PADDLE_HEIGHT
      ) {
        newDx = Math.abs(newDx) * 1.05
        const hitPos = (newY - playerPaddle.y) / PADDLE_HEIGHT
        newDy = (hitPos - 0.5) * 10
        newX = 20 + PADDLE_WIDTH
      }

      // AI paddle collision (right side with offset)
      if (
        newX + BALL_SIZE >= CANVAS_WIDTH - PADDLE_WIDTH - 20 &&
        newX <= CANVAS_WIDTH - 20 &&
        newY + BALL_SIZE >= aiPaddle.y &&
        newY <= aiPaddle.y + PADDLE_HEIGHT
      ) {
        newDx = -Math.abs(newDx) * 1.05
        const hitPos = (newY - aiPaddle.y) / PADDLE_HEIGHT
        newDy = (hitPos - 0.5) * 10
        newX = CANVAS_WIDTH - PADDLE_WIDTH - 20 - BALL_SIZE
      }

      // Score points
      if (newX <= 0) {
        // AI scores
        setAiScore(s => {
          const newAiScore = s + 1
          if (newAiScore >= 10) {
            setGameOver(true)
            setScore(currentScore => {
              saveGameScore(currentScore)
              return currentScore
            })
          }
          return newAiScore
        })
        return {
          x: CANVAS_WIDTH / 2,
          y: CANVAS_HEIGHT / 2,
          dx: -BALL_SPEED_INITIAL,
          dy: BALL_SPEED_INITIAL
        }
      }

      if (newX >= CANVAS_WIDTH - BALL_SIZE) {
        // Player scores
        setScore(s => s + 1)
        return {
          x: CANVAS_WIDTH / 2,
          y: CANVAS_HEIGHT / 2,
          dx: BALL_SPEED_INITIAL,
          dy: BALL_SPEED_INITIAL
        }
      }

      return { x: newX, y: newY, dx: newDx, dy: newDy }
    })
  }, [playerPaddle.y, aiPaddle.y, gameOver, isPaused, saveGameScore])

  useEffect(() => {
    loadRankings()
  }, [loadRankings])

  // Previne scroll durante o jogo
  useEffect(() => {
    document.body.classList.add('game-active')
    
    return () => {
      document.body.classList.remove('game-active')
    }
  }, [])

  useEffect(() => {
    if (showRanking && !gameOver) {
      setIsPaused(true)
    }
  }, [showRanking, gameOver])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'p' || e.key === 'P') {
        setIsPaused(prev => !prev)
        return
      }
      if (e.key === 'r' || e.key === 'R') {
        if (!gameOver && !showRanking) {
          setIsPaused(true)
        }
        setShowRanking(prev => !prev)
        return
      }
      keysPressed.current[e.key] = true
    }

    const handleKeyUp = (e) => {
      keysPressed.current[e.key] = false
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [gameOver, showRanking])

  useEffect(() => {
    if (gameOver || isPaused) return

    gameLoopRef.current = setInterval(() => {
      movePaddles()
      updateBall()
    }, 1000 / 60)

    return () => clearInterval(gameLoopRef.current)
  }, [movePaddles, updateBall, gameOver, isPaused])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const ctx = canvas.getContext('2d')
    
    // Clear canvas
    ctx.fillStyle = '#0a0a0a'
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT)

    // Center line
    ctx.strokeStyle = '#333'
    ctx.setLineDash([10, 10])
    ctx.beginPath()
    ctx.moveTo(CANVAS_WIDTH / 2, 0)
    ctx.lineTo(CANVAS_WIDTH / 2, CANVAS_HEIGHT)
    ctx.stroke()
    ctx.setLineDash([])

    // Player paddle (left)
    ctx.fillStyle = '#00eaff'
    ctx.shadowBlur = 20
    ctx.shadowColor = '#00eaff'
    ctx.fillRect(20, playerPaddle.y, PADDLE_WIDTH, PADDLE_HEIGHT)

    // AI paddle (right)
    ctx.fillStyle = '#ff0000'
    ctx.shadowBlur = 20
    ctx.shadowColor = '#ff0000'
    ctx.fillRect(CANVAS_WIDTH - PADDLE_WIDTH - 20, aiPaddle.y, PADDLE_WIDTH, PADDLE_HEIGHT)

    // Ball
    ctx.fillStyle = '#f0f000'
    ctx.shadowBlur = 20
    ctx.shadowColor = '#f0f000'
    ctx.beginPath()
    ctx.arc(ball.x + BALL_SIZE / 2, ball.y + BALL_SIZE / 2, BALL_SIZE / 2, 0, Math.PI * 2)
    ctx.fill()

    ctx.shadowBlur = 0
  }, [playerPaddle, aiPaddle, ball])

  // Touch controls para arrastar o paddle
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    let touchMoving = false

    const handleTouchStart = (e) => {
      if (gameOver || isPaused) return
      touchMoving = true
    }

    const handleTouchMove = (e) => {
      if (gameOver || isPaused || !touchMoving) return
      e.preventDefault()
      const touch = e.touches[0]
      const rect = canvas.getBoundingClientRect()
      const touchY = touch.clientY - rect.top
      const paddleY = Math.max(0, Math.min(CANVAS_HEIGHT - PADDLE_HEIGHT, touchY - PADDLE_HEIGHT / 2))
      setPlayerPaddle({ y: paddleY })
    }

    const handleTouchEnd = () => {
      touchMoving = false
    }

    canvas.addEventListener('touchstart', handleTouchStart)
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false })
    canvas.addEventListener('touchend', handleTouchEnd)

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart)
      canvas.removeEventListener('touchmove', handleTouchMove)
      canvas.removeEventListener('touchend', handleTouchEnd)
    }
  }, [gameOver, isPaused])

  function resetGame() {
    setScore(0)
    setAiScore(0)
    setGameOver(false)
    setIsPaused(false)
    setPlayerPaddle({ y: CANVAS_HEIGHT / 2 - PADDLE_HEIGHT / 2 })
    setAiPaddle({ y: CANVAS_HEIGHT / 2 - PADDLE_HEIGHT / 2 })
    setBall({
      x: CANVAS_WIDTH / 2,
      y: CANVAS_HEIGHT / 2,
      dx: BALL_SPEED_INITIAL,
      dy: BALL_SPEED_INITIAL
    })
    setShowRanking(false)
    setSavingScore(false)
    setScoreSaved(false)
  }

  const handleMobileUp = () => {
    mobileMovementRef.current.up = true
    setTimeout(() => {
      mobileMovementRef.current.up = false
    }, 100)
  }

  const handleMobileDown = () => {
    mobileMovementRef.current.down = true
    setTimeout(() => {
      mobileMovementRef.current.down = false
    }, 100)
  }

  const handleMobilePause = () => {
    if (!gameOver) {
      setIsPaused(prev => !prev)
    }
  }

  return (
    <div className="pong-container">
      <UserHeader user={user} gameName="PONG" />

      <div className="game-layout">
        <GameStats 
          score={score} 
          lines={aiScore} 
          level={1} 
        />

        <div className="game-area">
          <canvas 
            ref={canvasRef} 
            width={CANVAS_WIDTH} 
            height={CANVAS_HEIGHT}
            className="game-canvas"
          />
          
          {/* Info Mobile Overlay */}
          <div className="mobile-info-overlay">
            <span>👤 {score}</span>
            <span>🤖 {aiScore}</span>
          </div>
          
          {gameOver && (
            <div className="game-overlay">
              <div className="overlay-content">
                <h2>{score >= 10 ? 'VITÓRIA!' : 'DERROTA!'}</h2>
                <p className="final-score">Sua Pontuação: <strong>{score}</strong></p>
                <p className="final-stats">IA: {aiScore} pontos</p>
                
                {savingScore && (
                  <p className="saving-message">💾 Salvando pontuação...</p>
                )}
                
                {scoreSaved && (
                  <p className="saved-message">✅ Pontuação salva com sucesso!</p>
                )}
                
                <div className="game-over-buttons">
                  <button className="btn-primary" onClick={resetGame}>
                    🎮 Jogar Novamente
                  </button>
                  <button className="btn-secondary" onClick={() => setShowRanking(true)}>
                    🏆 Ver Rankings
                  </button>
                  {onBackToMenu && (
                    <button className="btn-secondary" onClick={onBackToMenu}>
                      ← Voltar ao Menu
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {isPaused && !gameOver && (
            <div className="game-overlay">
              <div className="overlay-content">
                <h2>PAUSADO</h2>
                <p>Pressione P para continuar</p>
              </div>
            </div>
          )}
        </div>

        <div className="sidebar right-sidebar">
          <div className="controls-panel">
            <h3>CONTROLES</h3>
            <div className="control-item">↑ ↓ Mover Raquete</div>
            <div className="control-item">P Pausar</div>
            <div className="control-item">R Ranking</div>
          </div>
          
          <div className="info-panel">
            <h3>REGRAS</h3>
            <div className="control-item">Primeiro a 10 vence</div>
            <div className="control-item">IA: Vermelho</div>
            <div className="control-item">Você: Azul</div>
          </div>
          
          {!gameOver && (
            <button className="btn-secondary" onClick={() => setIsPaused(!isPaused)}>
              {isPaused ? 'Continuar' : 'Pausar'}
            </button>
          )}
          
          {onBackToMenu && (
            <button className="btn-back-menu" onClick={onBackToMenu}>
              ← Menu
            </button>
          )}
          
          <button className="btn-secondary" onClick={resetGame}>
            Reiniciar
          </button>
          
          <button className="btn-secondary" onClick={() => {
            if (!gameOver && !showRanking) setIsPaused(true)
            setShowRanking(!showRanking)
          }}>
            {showRanking ? 'Fechar Ranking' : 'Ver Ranking'}
          </button>
        </div>
      </div>

      <RankingModal
        show={showRanking}
        onClose={() => setShowRanking(false)}
        globalRanking={globalRanking}
        personalRanking={personalRanking}
        currentUserId={user?.uid}
        gameOver={gameOver}
      />

      <MobileControls
        gameType="snake"
        onUp={handleMobileUp}
        onDown={handleMobileDown}
        onPause={handleMobilePause}
      />
    </div>
  )
}
